// Переговорные комнаты и NPC-визиты цикла: список чатов, панель
// комнаты, NPC-сцена, соглашения. Вынесено из CycleTab.tsx без
// изменения поведения (Фаза 1, разбор монолита).

import { useState, useEffect, type FormEvent } from "react";
import {
  api,
  type DiscussionRoom,
  type GameCycle,
  type MediaAsset,
  type NegotiationAgreement,
  type NpcVisit,
} from "../api";
import {
  Avatar,
  DeadlineValue,
  GAME_UI_ASSET_BASE,
  MockupSectionTitle,
  PlayerIdentity,
  clearDraft,
  formatTime,
  initials,
  npcVisitDeadlinePassed,
  previewText,
  readDraft,
  writeDraft,
  type Props,
  type Section,
} from "./shared";

function ActionNegotiationPanel(props: Props & { onNavigate?: (section: Section) => void }) {
  const cycle = props.setup.cycle.current!;
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const activeRoom = activeRoomId ? cycle.rooms.find((room) => room.id === activeRoomId) : undefined;
  const activeNpc = cycle.npcVisits.filter(
    (visit) => visit.status === "ACTIVE" && (visit.isInvited || visit.roomType === "NPC_PUBLIC") && !npcVisitDeadlinePassed(visit),
  ).length;
  return (
    <section className="cc-action-results-section cc-action-negotiations-panel cc-mockup-action-section">
      <MockupSectionTitle eyebrow="Чаты и встречи" title={activeNpc ? `${activeNpc} NPC ждёт ответа` : "Переговорные и встречи"} />
      {cycle.rooms.length === 0 ? (
        <p className="cc-empty-inline">Переговорных пока нет. Можно создать новую комнату для обсуждения или координации.</p>
      ) : (
        <div className="cc-chat-list compact" role="list">
          {cycle.rooms.slice(0, 12).map((room) => (
            <ChatRoomListItem
              campaignId={props.campaignId}
              cycle={cycle}
              room={room}
              key={room.id}
              onOpen={() => setActiveRoomId(room.id)}
            />
          ))}
        </div>
      )}
      <div className="cc-inline-actions">
        <CreateRoom {...props} />
      </div>
      {activeRoom && (
        <div className="cc-chat-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cc-chat-modal">
            <div className="cc-chat-modal-header">
              <div>
                <small>{activeRoom.roomType.startsWith("NPC_") ? "NPC-встреча" : "Переговорная"}</small>
                <strong>{activeRoom.title}</strong>
              </div>
              <button type="button" className="cc-modal-close" aria-label="Закрыть комнату" onClick={() => setActiveRoomId(null)}>×</button>
            </div>
            <div className="cc-chat-modal-body">
              <RoomPanel key={activeRoom.id} {...props} room={activeRoom} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
function ChatRoomListItem({
  campaignId,
  cycle,
  room,
  onOpen,
}: {
  campaignId: string;
  cycle: GameCycle;
  room: DiscussionRoom;
  onOpen: () => void;
}) {
  const lastMessage = room.messages[room.messages.length - 1];
  const visit = room.npcVisitId
    ? cycle.npcVisits.find((candidate) => candidate.id === room.npcVisitId)
    : undefined;
  const completedNpcMeeting =
    room.roomType.startsWith("NPC_") &&
    (Boolean(room.closedAt) || Boolean(visit && visit.status !== "ACTIVE"));
  const title = roomTitleForList(room, visit);
  const snippet = lastMessage
    ? `${lastMessage.senderDisplayName}: ${previewText(lastMessage.content, 76)}`
    : visit?.objectiveText
      ? previewText(visit.objectiveText, 76)
      : room.roomType === "PUBLIC"
        ? "Общий канал Совета"
        : "Переговорная пока без сообщений";
  const time = lastMessage
    ? formatTime(lastMessage.createdAt)
    : completedNpcMeeting
      ? "завершена"
      : room.roomType.startsWith("NPC_")
        ? visit?.statusLabel || "NPC"
        : "пусто";
  return (
    <button
      type="button"
      className={`cc-chat-list-row${room.unreadCount > 0 ? " unread" : ""}${completedNpcMeeting ? " completed" : ""}`}
      onClick={onOpen}
      role="listitem"
    >
      <span className={`cc-chat-list-avatar ${room.roomType.toLowerCase().replace("_", "-")}`}>
        {room.roomType.startsWith("NPC_") && visit ? (
          <NpcPortraitVisual campaignId={campaignId} visit={visit} />
        ) : room.roomType === "PUBLIC" ? (
          "◉"
        ) : (
          "🔒"
        )}
      </span>
      <span className="cc-chat-list-copy">
        <strong>{title}</strong>
        <small>{snippet}</small>
      </span>
      <span className="cc-chat-list-meta">
        <small>{time}</small>
        {room.unreadCount > 0 && <b>{room.unreadCount}</b>}
        {completedNpcMeeting && room.unreadCount === 0 && <em>завершена</em>}
      </span>
    </button>
  );
}
function roomTitleForList(room: DiscussionRoom, visit?: NpcVisit): string {
  if (room.roomType === "PUBLIC") return "Общий совет";
  if (visit) return `${visit.characterName} · ${visit.characterTitle}`;
  return room.title;
}
function CreateRoom({ campaignId, setup, action }: Props) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  if (!open)
    return (
      <button type="button" onClick={() => setOpen(true)}>
        Создать закрытую комнату
      </button>
    );
  return (
    <form
      className="cc-inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (title.trim().length < 3 || selected.length < 1) return;
        void (async () => {
          const saved = await action("Создаём закрытую комнату…", () =>
            api.createDiscussionRoom(campaignId, title, selected),
          );
          if (saved) {
            setTitle("");
            setSelected([]);
            setOpen(false);
          }
        })();
      }}
    >
      <label>
        Название
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
        />
      </label>
      <div
        className="cc-member-picker"
        role="group"
        aria-label="Участники закрытой комнаты"
      >
        {setup.members
          .filter((member) => !member.isCurrentUser)
          .map((member) => {
            const checked = selected.includes(member.userId);
            const displayName =
              member.displayName?.trim() ||
              (member.username ? `@${member.username}` : "Участник кампании");
            return (
              <label
                className={checked ? "selected" : ""}
                key={member.userId}
                title={displayName}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  aria-label={`Добавить ${displayName} в комнату`}
                  onChange={(event) =>
                    setSelected((current) =>
                      event.target.checked
                        ? [...current, member.userId]
                        : current.filter((id) => id !== member.userId),
                    )
                  }
                />
                <PlayerIdentity
                  campaignId={campaignId}
                  memberId={member.campaignMemberId}
                  name={displayName}
                  avatar={member.avatar}
                />
              </label>
            );
          })}
      </div>
      <div className="cc-inline-actions">
        <button className="primary">Создать</button>
        <button type="button" onClick={() => setOpen(false)}>
          Отмена
        </button>
      </div>
    </form>
  );
}
// Общий кэш object-URL по картинке. Картинки отдаёт защищённый эндпоинт (нужен
// auth-заголовок), поэтому их нельзя показать через обычный <img src> — фронт качает
// бинарь и делает object URL. Без кэша герой и превью одной картинки грузятся дважды,
// а листание карусели каждый раз перекачивает blob (мигание). Кэш по assetId: первый
// показ качает, остальные (герой↔превью, возврат к кадру) берут из кэша — мгновенно.
// Ограничен по размеру, старые URL вытесняются и освобождаются.
const assetUrlCache = new Map<string, string>();
const ASSET_URL_CACHE_LIMIT = 60;

function cacheAssetUrl(key: string, url: string) {
  assetUrlCache.set(key, url);
  while (assetUrlCache.size > ASSET_URL_CACHE_LIMIT) {
    const oldest = assetUrlCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    const stale = assetUrlCache.get(oldest);
    assetUrlCache.delete(oldest);
    if (stale) URL.revokeObjectURL(stale);
  }
}

function useAssetContent(campaignId: string, asset?: MediaAsset) {
  const key = asset ? `${campaignId}:${asset.id}` : "";
  const [src, setSrc] = useState<string | null>(() => (key ? assetUrlCache.get(key) ?? null : null));

  useEffect(() => {
    if (!asset || asset.status !== "READY" || !key) {
      setSrc(null);
      return;
    }
    const cached = assetUrlCache.get(key);
    if (cached) {
      setSrc(cached);
      return;
    }
    let cancelled = false;
    void api
      .cycleImageContent(campaignId, asset.id)
      .then((blob) => {
        if (cancelled) return;
        const existing = assetUrlCache.get(key);
        if (existing) {
          setSrc(existing);
          return;
        }
        const url = URL.createObjectURL(blob);
        cacheAssetUrl(key, url);
        setSrc(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [asset?.id, asset?.status, campaignId, key]);

  return src;
}
function NpcPortraitVisual({
  campaignId,
  visit,
}: {
  campaignId: string;
  visit: NpcVisit;
}) {
  const asset = visit.portraitImages?.[0];
  const src = useAssetContent(campaignId, asset);
  if (src) {
    return <img src={src} alt={`Портрет: ${visit.characterName}`} />;
  }
  return <span>{initials(visit.characterName)}</span>;
}
function NpcRoomHero({
  campaignId,
  visit,
  canManage,
  action,
}: {
  campaignId: string;
  visit: NpcVisit;
  canManage: boolean;
  action: Props["action"];
}) {
  const sceneAsset = visit.roomSceneImages?.[0];
  const portraitAsset = visit.portraitImages?.[0];
  const sceneSrc = useAssetContent(campaignId, sceneAsset);
  const portraitSrc = useAssetContent(campaignId, portraitAsset);
  const regenerate = (asset: MediaAsset, label: string) =>
    void action(label, () => api.regenerateCycleImage(campaignId, asset.id));
  return (
    <div className="cc-npc-hero-card">
      <div className={`cc-npc-hero-scene${sceneSrc ? " ready" : sceneAsset?.status === "FAILED" ? " failed" : ""}`}>
        {sceneSrc ? (
          <img src={sceneSrc} alt={`Сцена встречи: ${visit.characterName}`} />
        ) : (
          <div className="cc-npc-hero-placeholder">
            <img src={`${GAME_UI_ASSET_BASE}/icon_room_npc.png`} alt="" />
            <div>
              <strong>
                {sceneAsset?.status === "FAILED"
                  ? "Сцена встречи не создана"
                  : "Сцена встречи готовится"}
              </strong>
              <small>
                {sceneAsset?.status === "FAILED"
                  ? "Комната работает и без изображения"
                  : "Иллюстрация переговорной появится автоматически"}
              </small>
            </div>
          </div>
        )}
        {canManage && sceneAsset?.status === "FAILED" && (
          <button
            type="button"
            className="cc-image-regenerate"
            onClick={() => regenerate(sceneAsset, "Повторяем создание сцены NPC…")}
          >
            Повторить создание
          </button>
        )}
      </div>
      <div className="cc-npc-hero-overlay">
        <div className="cc-npc-portrait large">
          {portraitSrc ? (
            <img src={portraitSrc} alt={`Портрет: ${visit.characterName}`} />
          ) : (
            <span>{initials(visit.characterName)}</span>
          )}
        </div>
        <div className="cc-npc-hero-meta">
          <small>Персонаж встречи</small>
          <strong>{visit.characterName}</strong>
          <span>{visit.characterTitle}</span>
          {canManage && portraitAsset?.status === "FAILED" && (
            <button
              type="button"
              className="cc-image-regenerate"
              onClick={() => regenerate(portraitAsset, "Повторяем создание портрета NPC…")}
            >
              Повторить портрет
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
function RoomPanel({
  campaignId,
  setup,
  action,
  room,
}: Props & { room: DiscussionRoom }) {
  const cycle = setup.cycle.current!;
  const messageKey = `cc:${campaignId}:${cycle.id}:room:${room.id}:message`;
  const agreementKey = `cc:${campaignId}:${cycle.id}:room:${room.id}:agreement`;
  const [message, setMessage] = useState(
    () => readDraft(messageKey, { value: "" }).value,
  );
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [agreementDraft, setAgreementDraft] = useState(() =>
    readDraft(agreementKey, { title: "", terms: "" }),
  );
  useEffect(
    () => writeDraft(messageKey, { value: message }),
    [messageKey, message],
  );
  useEffect(
    () => writeDraft(agreementKey, agreementDraft),
    [agreementKey, agreementDraft],
  );

  async function sendMessage(event: FormEvent) {
    event.preventDefault();
    if (!message.trim()) return;
    const sent = await action("Отправляем сообщение…", () =>
      api.sendDiscussionMessage(campaignId, room.id, message),
    );
    if (sent) {
      setMessage("");
      clearDraft(messageKey);
    }
  }

  async function saveAgreement(event: FormEvent) {
    event.preventDefault();
    if (
      agreementDraft.title.trim().length < 5 ||
      agreementDraft.terms.trim().length < 20
    )
      return;
    const saved = await action("Фиксируем договорённость…", () =>
      api.createNegotiationAgreement(campaignId, room.id, {
        title: agreementDraft.title,
        terms: agreementDraft.terms,
        participantUserIds: room.members.map((member) => member.userId),
      }),
    );
    if (saved) {
      setAgreementDraft({ title: "", terms: "" });
      clearDraft(agreementKey);
      setAgreementOpen(false);
    }
  }

  const npcVisit = room.npcVisitId
    ? cycle.npcVisits.find((visit) => visit.id === room.npcVisitId)
    : undefined;
  const npcRoom = room.roomType.startsWith("NPC_");
  const npcActive = Boolean(npcVisit && npcVisitIsOpen(npcVisit, room));
  const npcTerminal =
    npcVisit && !npcActive ? npcVisitTerminalInfo(npcVisit) : undefined;
  const canPostMessage = npcRoom
    ? Boolean(npcVisit && npcVisitIsOpen(npcVisit, room))
    : room.canPost;
  return (
    <section className="cc-block cc-room-panel">
      <div className="cc-room-header">
        <div>
          <small>
            {npcRoom
              ? npcActive
                ? "Временная встреча"
                : "Завершённая встреча"
              : room.roomType === "PUBLIC"
                ? "Общая комната"
                : "Закрытая комната"}
          </small>
          <h2>{room.title}</h2>
        </div>
        <div className="cc-avatar-stack">
          {room.members.map((member) => (
            <Avatar
              campaignId={campaignId}
              memberId={member.campaignMemberId}
              name={member.displayName}
              avatar={member.avatar}
              key={member.userId}
            />
          ))}
        </div>
      </div>
      {npcVisit && (
        <>
          <NpcRoomHero
            campaignId={campaignId}
            visit={npcVisit}
            canManage={setup.canManage}
            action={action}
          />
        <div className={`cc-npc-room-context${npcActive ? "" : " finished"}`}>
          <div>
            <small>{npcActive ? "Стадия встречи" : "Итог встречи"}</small>
            <strong>
              {npcActive ? npcVisit.dialogueStageLabel : npcVisit.statusLabel}
            </strong>
            <span>{npcVisit.objectiveText}</span>
          </div>
          {npcVisit.offerGivesText && (
            <div className="cc-npc-offer">
              <b>{npcActive ? "Условия" : "Что обсуждалось"}</b>
              <span>
                <strong>Что предлагает:</strong> {npcVisit.offerGivesText}
              </span>
              <span>
                <strong>Что просит:</strong> {npcVisit.offerRequestsText}
              </span>
              {npcVisit.offerDurationText && (
                <span>
                  <strong>Срок:</strong> {npcVisit.offerDurationText}
                </span>
              )}
            </div>
          )}
          {npcActive && (
            <div className="cc-npc-dialogue-rule">
              <strong>Исход определит разговор</strong>
              <span>
                Сформулируйте согласие, отказ или встречные условия своими
                словами. Персонаж учитывает весь ход беседы и может согласиться
                полностью, ограничить помощь либо уйти.
              </span>
            </div>
          )}
          {npcActive && npcVisit.expiresAt && (
            <DeadlineValue
              label="Встреча закончится через"
              value={npcVisit.expiresAt}
            />
          )}
        </div>
        </>
      )}
      {npcTerminal && (
        <div className={`cc-npc-terminal ${npcTerminal.tone}`}>
          <strong>{npcTerminal.title}</strong>
          <p>{npcTerminal.text}</p>
          {npcTerminal.consequence && <span>{npcTerminal.consequence}</span>}
        </div>
      )}
      <div className="cc-room-messages">
        {room.messages.length === 0 ? (
          <p className="cc-empty-inline">Сообщений пока нет.</p>
        ) : (
          room.messages.map((item) => (
            <article
              className={`${item.isMine ? "mine" : ""}${item.senderType === "NPC" ? " npc" : ""}${item.senderType === "SYSTEM" ? " system" : ""}`}
              key={item.id}
            >
              {item.senderType === "SYSTEM" ? (
                <span className="cc-mini-avatar system">✓</span>
              ) : item.senderAvatar && item.senderCampaignMemberId ? (
                <Avatar
                  campaignId={campaignId}
                  memberId={item.senderCampaignMemberId}
                  name={item.senderDisplayName}
                  avatar={item.senderAvatar}
                />
              ) : (
                <span className="cc-mini-avatar npc">NPC</span>
              )}
              <div>
                <header>
                  <strong>{item.senderDisplayName}</strong>
                  <small>{formatTime(item.createdAt)}</small>
                </header>
                <p className="cc-dialogue-speech">{item.content}</p>
                {item.actionText && (
                  <p className="cc-dialogue-action">{item.actionText}</p>
                )}
              </div>
            </article>
          ))
        )}
      </div>
      {canPostMessage ? (
        <form className="cc-message-form" onSubmit={sendMessage}>
          <textarea
            rows={3}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={
              npcRoom
                ? "Задайте вопрос, укажите на проблему в условиях или предложите изменение"
                : "Напишите сообщение участникам комнаты"
            }
          />
          <div className="cc-draft-status">
            <span>
              {message.trim()
                ? "Черновик сохраняется автоматически"
                : "Черновик пуст"}
            </span>
            <button className="primary" disabled={!message.trim()}>
              Отправить
            </button>
          </div>
        </form>
      ) : (
        <div className="cc-room-closed-note">
          <strong>{npcRoom ? "Встреча закрыта" : "Комната закрыта"}</strong>
          <span>
            История переписки сохранена, новые сообщения отправить нельзя.
          </span>
        </div>
      )}
      {!npcRoom && (
        <>
          <div className="cc-agreements">
            <div className="cc-section-mini">
              <strong>Зафиксированные договорённости</strong>
              <button
                type="button"
                onClick={() => setAgreementOpen((value) => !value)}
              >
                + Зафиксировать
              </button>
            </div>
            {room.agreements.map((item) => (
              <AgreementCard
                item={item}
                campaignId={campaignId}
                key={item.id}
              />
            ))}
          </div>
          {agreementOpen && (
            <form className="cc-inline-form" onSubmit={saveAgreement}>
              <label>
                Краткое название
                <input
                  value={agreementDraft.title}
                  onChange={(event) =>
                    setAgreementDraft((current) => ({
                      ...current,
                      title: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                Что обязуются сделать стороны
                <textarea
                  rows={4}
                  value={agreementDraft.terms}
                  onChange={(event) =>
                    setAgreementDraft((current) => ({
                      ...current,
                      terms: event.target.value,
                    }))
                  }
                />
              </label>
              <button className="primary">Сохранить договорённость</button>
            </form>
          )}
        </>
      )}
    </section>
  );
}
function AgreementCard({
  item,
  campaignId,
}: {
  item: NegotiationAgreement;
  campaignId?: string;
}) {
  return (
    <article className="cc-agreement-card">
      <header>
        <strong>{item.title}</strong>
        {item.linkedInitiativeId && <span>прикреплена</span>}
      </header>
      <p>{item.terms}</p>
      {campaignId ? (
        <PlayerIdentity
          campaignId={campaignId}
          memberId={item.createdByCampaignMemberId}
          name={item.createdByDisplayName}
          avatar={item.createdByAvatar}
        />
      ) : (
        <small>Зафиксировал: {item.createdByDisplayName}</small>
      )}
    </article>
  );
}
function npcVisitTerminalInfo(visit: NpcVisit): {
  title: string;
  text: string;
  consequence?: string;
  tone: "accepted" | "partial" | "missed" | "neutral";
} {
  if (visit.terminalTitle && visit.terminalText)
    return {
      title: visit.terminalTitle,
      text: visit.terminalText,
      consequence: visit.terminalConsequence,
      tone:
        visit.terminalTone === "ACCEPTED"
          ? "accepted"
          : visit.terminalTone === "PARTIAL"
            ? "partial"
            : visit.terminalTone === "NEUTRAL"
              ? "neutral"
              : "missed",
    };
  if (npcVisitDeadlinePassed(visit))
    return {
      title: "Время встречи истекло",
      text: `${visit.characterName} больше не находится в переговорной: стороны не успели прийти к соглашению. Комната сохранена только как история разговора.`,
      consequence:
        visit.missedNarrative ||
        "Если соглашение не было достигнуто разговором, возможность считается упущенной.",
      tone: "missed",
    };
  if (visit.dialogueState === "PARTIAL_ACCEPTED")
    return {
      title: "Встреча завершена — достигнут частичный результат",
      text: `${visit.characterName} согласился только на ограниченный вариант сотрудничества и покинул переговорную. Полный эффект исходного предложения не применяется.`,
      consequence:
        "Предварительная договорённость сохранена в долгосрочных линиях мира и может получить развитие позднее.",
      tone: "partial",
    };
  if (visit.status === "RESOLVED" || visit.dialogueState === "ACCEPTED")
    return {
      title: "Встреча завершена — достигнута договорённость",
      text: `Стороны пришли к соглашению в ходе разговора. ${visit.characterName} больше не находится в переговорной, и новые сообщения в этой комнате не принимаются.`,
      consequence:
        visit.outcomeNarrative ||
        "Согласованный результат уже вступил в силу и будет отражён в летописи.",
      tone: "accepted",
    };
  if (visit.status === "EXPIRED" || visit.dialogueState === "EXPIRED")
    return {
      title: "Время встречи истекло",
      text: `${visit.characterName} больше не находится в переговорной: стороны не успели прийти к соглашению. Комната сохранена только как история разговора.`,
      consequence: visit.missedNarrative,
      tone: "missed",
    };
  return {
    title: "Встреча завершена без сделки",
    text: `${visit.characterName} больше не находится в переговорной. Обсуждение закрыто, отправить новое сообщение нельзя.`,
    consequence: visit.missedNarrative,
    tone: "missed",
  };
}
function npcVisitIsOpen(visit: NpcVisit, room: DiscussionRoom): boolean {
  return (
    visit.status === "ACTIVE" &&
    room.canPost &&
    !room.closedAt &&
    !npcVisitDeadlinePassed(visit)
  );
}

export {
  ActionNegotiationPanel,
  ChatRoomListItem,
  roomTitleForList,
  CreateRoom,
  RoomPanel,
  NpcRoomHero,
  NpcPortraitVisual,
  AgreementCard,
  useAssetContent,
  npcVisitIsOpen,
  npcVisitTerminalInfo,
};
